import React, { createContext, ReactNode, useContext, useEffect, useRef } from "react";
import Head from "next/head";
import { useRouter } from "next/router";

import { TWITTER_HANDLE } from "@vahak/core/dist/constants/socialMediaLinks";

import { buildPageSeo, PageSeoConfig } from "../../methods/pageSeo";

const SeoContext = createContext<React.MutableRefObject<PageSeoConfig | null> | null>(null);

export function SeoProvider({ children }: { children: ReactNode }) {
    const pageConfigRef = useRef<PageSeoConfig | null>(null);

    return <SeoContext.Provider value={pageConfigRef}>{children}</SeoContext.Provider>;
}

export function useRegisterPageSeo(config: PageSeoConfig) {
    const pageConfigRef = useContext(SeoContext);

    if (!pageConfigRef) {
        return;
    }

    pageConfigRef.current = config;

    useEffect(() => {
        pageConfigRef.current = config;

        return () => {
            if (pageConfigRef.current === config) {
                pageConfigRef.current = null;
            }
        };
    }, [pageConfigRef, config]);
}

export function GlobalSeoHead() {
    const router = useRouter();
    const pageConfigRef = useContext(SeoContext);

    const resolved = buildPageSeo(pageConfigRef?.current ?? null, {
        asPath: router.asPath,
        locale: router.locale ?? "en"
    });

    if (!resolved) {
        return null;
    }

    const hasPageMeta = Boolean(resolved.title);
    const shouldRenderRobots = hasPageMeta || resolved.shouldBeIndexed === false;

    return (
        <Head>
            {hasPageMeta && resolved.title ? <title>{resolved.title}</title> : null}
            {hasPageMeta && resolved.description ? <meta name="description" content={resolved.description} /> : null}
            {hasPageMeta && resolved.keywords ? <meta name="keywords" content={resolved.keywords} /> : null}
            {shouldRenderRobots ? (
                <meta name="robots" content={resolved.shouldBeIndexed ? "index, follow" : "noindex, nofollow"} />
            ) : null}
            {hasPageMeta ? <link rel="canonical" href={resolved.canonicalUrl} /> : null}
            {hasPageMeta ? <meta property="og:url" content={resolved.ogUrl} /> : null}
            {hasPageMeta && resolved.title ? <meta property="og:title" content={resolved.title} /> : null}
            {hasPageMeta && resolved.ogDescription ? (
                <meta property="og:description" content={resolved.ogDescription} />
            ) : null}
            {hasPageMeta ? <meta property="og:image" content={resolved.ogImgSrc} /> : null}
            {hasPageMeta && resolved.ogImgAltText ? (
                <meta property="og:image:alt" content={resolved.ogImgAltText} />
            ) : null}
            {hasPageMeta && resolved.ogType ? <meta property="og:type" content={resolved.ogType} /> : null}
            {hasPageMeta && resolved.ogSiteName ? <meta property="og:site_name" content={resolved.ogSiteName} /> : null}
            {hasPageMeta && resolved.ogLocale ? <meta property="og:locale" content={resolved.ogLocale} /> : null}
            {hasPageMeta
                ? resolved.ogLocaleAlternates.map((locale) => (
                      <meta key={locale} property="og:locale:alternate" content={locale} />
                  ))
                : null}
            {hasPageMeta && resolved.title ? <meta name="twitter:card" content="summary_large_image" /> : null}
            {hasPageMeta ? <meta name="twitter:site" content={TWITTER_HANDLE} /> : null}
            {hasPageMeta && resolved.title ? <meta name="twitter:title" content={resolved.title} /> : null}
            {hasPageMeta && resolved.ogDescription ? (
                <meta name="twitter:description" content={resolved.ogDescription} />
            ) : null}
            {hasPageMeta ? <meta name="twitter:image" content={resolved.ogImgSrc} /> : null}
            {resolved.hreflangAlternates.map(({ hreflang, href }) => (
                <link key={hreflang} rel="alternate" hrefLang={hreflang} href={href} />
            ))}
            <link rel="alternate" hrefLang="x-default" href={resolved.hreflangDefault} />
            {hasPageMeta
                ? resolved.schemaScripts.map(({ id, json }) => (
                      <script key={id} type="application/ld+json" id={id} dangerouslySetInnerHTML={{ __html: json }} />
                  ))
                : null}
        </Head>
    );
}
